import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout, useTheme } from "@/components/DashboardLayout";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Plus, Eye, RefreshCw, Filter, FileText, List, LayoutGrid,
  Clock, CheckCircle, XCircle, AlertCircle, Loader2, ArrowUpDown
} from "lucide-react";

const STATUS_COLORS = {
  draft: { label: "Draft", dark: "bg-slate-700 text-slate-200", light: "bg-gray-100 text-gray-700" },
  submitted: { label: "Submitted", dark: "bg-blue-900/50 text-blue-300", light: "bg-blue-100 text-blue-700" },
  received: { label: "Received", dark: "bg-indigo-900/50 text-indigo-300", light: "bg-indigo-100 text-indigo-700" },
  under_review: { label: "Under Review", dark: "bg-amber-900/50 text-amber-300", light: "bg-amber-100 text-amber-700" },
  pended: { label: "Pended", dark: "bg-orange-900/50 text-orange-300", light: "bg-orange-100 text-orange-700" },
  approved: { label: "Approved", dark: "bg-emerald-900/50 text-emerald-300", light: "bg-green-100 text-green-700" },
  denied: { label: "Denied", dark: "bg-red-900/50 text-red-300", light: "bg-red-100 text-red-700" },
  more_info_required: { label: "More Info", dark: "bg-yellow-900/50 text-yellow-300", light: "bg-yellow-100 text-yellow-700" },
  appealed: { label: "Appealed", dark: "bg-purple-900/50 text-purple-300", light: "bg-purple-100 text-purple-700" },
  cancelled: { label: "Cancelled", dark: "bg-slate-800 text-slate-400", light: "bg-gray-100 text-gray-500" },
};
const STATUS_ICONS = { draft: FileText, submitted: Clock, received: Clock, under_review: AlertCircle, pended: AlertCircle, approved: CheckCircle, denied: XCircle, more_info_required: AlertCircle, appealed: RefreshCw, cancelled: XCircle };

function PARequestsContent() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => { fetchRequests(); }, []);
  const fetchRequests = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pa-requests`, { withCredentials: true }); setRequests(res.data.requests || res.data || []); }
    catch { } finally { setLoading(false); }
  };

  const filtered = requests.filter(r => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = `${r.patient_first_name || ""} ${r.patient_last_name || ""}`.toLowerCase();
      return name.includes(q) || r.pa_id?.toLowerCase().includes(q) || r.member_id?.toLowerCase().includes(q) || r.service_type?.toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === "newest") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sortBy === "oldest") return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    if (sortBy === "status") return (a.status || "").localeCompare(b.status || "");
    if (sortBy === "patient") return (`${a.patient_first_name} ${a.patient_last_name}`).localeCompare(`${b.patient_first_name} ${b.patient_last_name}`);
    return 0;
  });

  const getSC = (status) => STATUS_COLORS[status] || STATUS_COLORS.draft;

  const changeStatus = async (paId, newStatus) => {
    try {
      await axios.put(`${API}/pa-requests/${paId}`, { status: newStatus }, { withCredentials: true });
      setRequests(prev => prev.map(r => r.pa_id === paId ? { ...r, status: newStatus, updated_at: new Date().toISOString() } : r));
      toast.success(`Status updated to ${STATUS_COLORS[newStatus]?.label || newStatus}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const getReferralStatus = (req) => {
    const c = req.rendering_provider?.confirmation;
    if (!c) return { label: "Not Sent", color: "bg-gray-200 text-gray-600" };
    const s = c.status || "";
    if (s.includes("Accepted")) return { label: "Accepted", color: "bg-emerald-100 text-emerald-700" };
    if (s.includes("Not Accepting")) return { label: "Declined", color: "bg-red-100 text-red-700" };
    if (s.includes("Reminder")) return { label: "Reminded", color: "bg-amber-100 text-amber-700" };
    if (s.includes("Awaiting")) return { label: "Awaiting Response", color: "bg-blue-100 text-blue-700" };
    return { label: s || "Pending", color: "bg-slate-100 text-slate-700" };
  };
  const getSI = (status) => STATUS_ICONS[status] || FileText;
  const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

  // Theme helpers
  const c = {
    banner: isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-[#f0f7ff] border-[#b8d4f0]",
    bannerTitle: isDark ? "text-emerald-400" : "text-[#0066a1]",
    bannerDesc: isDark ? "text-slate-400" : "text-gray-600",
    btn: isDark ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-[#0066a1] hover:bg-[#005080] text-white",
    card: isDark ? "bg-slate-900/70 border-slate-800" : "bg-white border-gray-200",
    input: isDark ? "bg-slate-800 border-slate-700 text-white" : "border-gray-300 text-gray-800",
    selectTrigger: isDark ? "border-slate-700 text-slate-300" : "border-gray-300 text-gray-700",
    viewActive: isDark ? "bg-emerald-600 text-white" : "bg-[#0066a1] text-white",
    viewInactive: isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-white text-gray-500 hover:bg-gray-50",
    viewBorder: isDark ? "border-slate-700" : "border-gray-300",
    muted: isDark ? "text-slate-500" : "text-gray-400",
    thead: isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200",
    th: isDark ? "text-slate-400" : "text-gray-600",
    row: isDark ? "border-slate-800 hover:bg-slate-800/50" : "border-gray-100 hover:bg-blue-50/30",
    cellMono: isDark ? "text-emerald-400" : "text-[#0066a1]",
    cellText: isDark ? "text-white" : "text-gray-800",
    cellMuted: isDark ? "text-slate-400" : "text-gray-600",
    viewBtn: isDark ? "text-emerald-400 hover:bg-emerald-500/10" : "text-[#0066a1] hover:bg-blue-50",
    cardHover: isDark ? "border-slate-700 hover:border-emerald-500/50 hover:shadow-emerald-500/10" : "border-gray-200 hover:shadow-md",
    cardBg: isDark ? "bg-slate-900/70" : "bg-white",
    empty: isDark ? "text-slate-500" : "text-gray-400",
    emptyIcon: isDark ? "text-slate-600" : "text-gray-300",
    refreshBtn: isDark ? "border-slate-700 text-slate-400" : "border-gray-300 text-gray-600",
  };

  return (
    <div className="space-y-4" data-testid="auth-referral-dashboard">
      {/* Header */}
      <div className={`${c.banner} border rounded-lg p-4`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className={`text-xl font-bold ${c.bannerTitle}`}>Auth/Referral Dashboard</h1>
            <p className={`text-sm ${c.bannerDesc}`}>Check status of submitted requests, update or cancel pended authorizations</p>
          </div>
          <Button onClick={() => navigate("/pa-requests/new")} className={c.btn} data-testid="new-request-btn">
            <Plus className="w-4 h-4 mr-2" /> New Request
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className={`${c.card} rounded-lg border shadow-sm p-4`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${c.muted}`} />
            <Input placeholder="Search by patient name, cert #, member ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`pl-9 text-sm ${c.input}`} data-testid="search-input" />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className={`w-40 text-sm ${c.selectTrigger}`}><ArrowUpDown className={`w-3.5 h-3.5 mr-1 ${c.muted}`} /><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="newest">Newest First</SelectItem><SelectItem value="oldest">Oldest First</SelectItem><SelectItem value="status">By Status</SelectItem><SelectItem value="patient">By Patient</SelectItem></SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className={`w-40 text-sm ${c.selectTrigger}`}><Filter className={`w-3.5 h-3.5 mr-1 ${c.muted}`} /><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{Object.entries(STATUS_COLORS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className={`flex border ${c.viewBorder} rounded-md overflow-hidden`}>
            <button onClick={() => setViewMode("list")} className={`p-2 ${viewMode === "list" ? c.viewActive : c.viewInactive}`} data-testid="list-view-btn"><List className="w-4 h-4" /></button>
            <button onClick={() => setViewMode("detail")} className={`p-2 ${viewMode === "detail" ? c.viewActive : c.viewInactive}`} data-testid="detail-view-btn"><LayoutGrid className="w-4 h-4" /></button>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests} className={c.refreshBtn} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <p className={`text-xs ${c.muted} mt-3`}>{filtered.length} authorization(s) found</p>
      </div>

      {loading && <div className="flex items-center justify-center py-12"><Loader2 className={`w-8 h-8 ${c.cellMono} animate-spin`} /></div>}

      {/* List View */}
      {!loading && viewMode === "list" && (
        <div className={`${c.card} rounded-lg border shadow-sm overflow-hidden`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`${c.thead} border-b`}>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Type</th>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Cert #</th>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Patient ID</th>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Member ID</th>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Payer</th>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Submitted On</th>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Status</th>
                <th className={`text-left px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Referral Status</th>
                <th className={`text-center px-3 py-3 text-xs font-bold ${c.th} uppercase`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-12"><FileText className={`w-10 h-10 mx-auto mb-2 ${c.emptyIcon}`} /><p className={c.empty}>No authorization requests found</p></td></tr>
              ) : filtered.map((req) => {
                const rs = getReferralStatus(req);
                return (
                  <tr key={req.pa_id} className={`border-b ${c.row}`} data-testid={`pa-row-${req.pa_id}`}>
                    <td className="px-3 py-3"><Badge variant="outline" className="text-xs font-normal">{req.auth_type || "Outpatient"}</Badge></td>
                    <td className={`px-3 py-3 font-mono text-xs ${c.cellMono}`}>{req.cert_number || req.pa_id?.slice(0, 12) || "—"}</td>
                    <td className={`px-3 py-3 ${c.cellMuted}`}>
                      <span className={`font-mono text-xs ${c.cellMono}`}>{req.patient_id || "—"}</span>
                      {(req.patient_first_name || req.patient_last_name) && (
                        <span className={`block text-[10px] ${c.muted}`}>{`${req.patient_first_name || ""} ${req.patient_last_name || ""}`.trim()}</span>
                      )}
                    </td>
                    <td className={`px-3 py-3 font-mono text-xs ${c.cellMono}`}>{req.member_id || "—"}</td>
                    <td className={`px-3 py-3 ${c.cellMuted}`}>{req.payer || req.insurer || "—"}{req.plan_type ? <span className={`block text-[10px] ${c.muted}`}>{req.plan_type}</span> : null}</td>
                    <td className={`px-3 py-3 ${c.cellMuted}`}>{fmtDate(req.created_at)}</td>
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <Select value={req.status || "draft"} onValueChange={(v) => changeStatus(req.pa_id, v)}>
                        <SelectTrigger className={`h-7 text-xs px-2 w-36 ${c.selectTrigger}`} data-testid={`status-select-${req.pa_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_COLORS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${rs.color}`}>{rs.label}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <Button variant="ghost" size="sm" className={c.viewBtn} onClick={() => navigate(`/pa-requests/${req.pa_id}/workflow`)} data-testid={`view-btn-${req.pa_id}`}><Eye className="w-4 h-4 mr-1" /> View</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail/Grid View */}
      {!loading && viewMode === "detail" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 ? (
            <div className="col-span-full text-center py-12"><FileText className={`w-10 h-10 mx-auto mb-2 ${c.emptyIcon}`} /><p className={c.empty}>No authorization requests found</p></div>
          ) : filtered.map((req) => {
            const sc = getSC(req.status);
            const SI = getSI(req.status);
            return (
              <div key={req.pa_id} className={`${c.cardBg} rounded-lg border ${c.cardHover} shadow-sm hover:shadow-md transition-shadow cursor-pointer`} onClick={() => navigate(`/pa-requests/${req.pa_id}/workflow`)} data-testid={`pa-card-${req.pa_id}`}>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={`${isDark ? sc.dark : sc.light} text-xs`}><SI className="w-3 h-3 mr-1" /> {sc.label}</Badge>
                    <span className={`font-mono text-xs ${c.muted}`}>{req.pa_id?.slice(0, 12)}</span>
                  </div>
                  <h3 className={`font-semibold ${c.cellText}`}>{req.patient_first_name ? `${req.patient_first_name} ${req.patient_last_name}` : "—"}</h3>
                  <p className={`text-sm ${c.cellMuted} mt-1`}>{req.service_type || "—"}</p>
                  <div className={`flex items-center justify-between mt-3 pt-3 border-t ${isDark ? "border-slate-800" : "border-gray-100"} text-xs ${c.muted}`}>
                    <span>{req.payer || req.insurer || "—"}</span>
                    <span>{fmtDate(req.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PARequests() {
  return <DashboardLayout><PARequestsContent /></DashboardLayout>;
}
